import { defineStore } from 'pinia'
import { ref } from 'vue'
import companyGuidelinesApi from '@src/api/web/companyGuidelines'
import useRealmSettings from '@src/composables/realmSettings'
import useUser from '@src/composables/user'
import { computedAsync } from '@vueuse/core'

export enum UserGuideListType {
    WHATS_NEW = 'whats-new',
    COMPANY_GUIDELINES = 'company-guidelines',
    GETTING_STARTED = 'getting-started',
    LEAVE_FEEDBACK = 'leave-feedback',
    ABOUT_STARMIND = 'about-starmind'
}

export type UserGuideIds =
    UserGuideListType.WHATS_NEW |
    UserGuideListType.COMPANY_GUIDELINES |
    UserGuideListType.GETTING_STARTED |
    UserGuideListType.LEAVE_FEEDBACK |
    UserGuideListType.ABOUT_STARMIND

export interface IUserGuideListItem{
    id: UserGuideIds;
    title: string;
    text: string;
    tracking: {
        label: string;
        menuItemId: number;
    };
    badge?: string;
    icon?: string;
}

export const initialListItems: IUserGuideListItem[] = [
  {
    id: UserGuideListType.WHATS_NEW,
    title: 'user-guide.sidenav.whats-new.title',
    text: 'user-guide.sidenav.whats-new.text',
    badge: '',
    tracking: {
      label: 'latest_changes',
      menuItemId: 1
    }
  },
  {
    id: UserGuideListType.COMPANY_GUIDELINES,
    title: 'user-guide.sidenav.company-guidelines.title',
    text: 'user-guide.sidenav.company-guidelines.text',
    tracking: {
      label: 'company_guidelines',
      menuItemId: 2
    }
  },
  {
    id: UserGuideListType.GETTING_STARTED,
    title: 'user-guide.sidenav.getting-started.title',
    text: 'user-guide.sidenav.getting-started.text',
    tracking: {
      label: 'getting_started',
      menuItemId: 3
    }
  },
  {
    id: UserGuideListType.LEAVE_FEEDBACK,
    title: 'user-guide.sidenav.leave-feedback.title',
    text: 'user-guide.sidenav.leave-feedback.text',
    tracking: {
      label: 'feedback',
      menuItemId: 4
    }
  },
  {
    id: UserGuideListType.ABOUT_STARMIND,
    title: 'user-guide.sidenav.about-starmind.title',
    text: 'user-guide.sidenav.about-starmind.text',
    icon: 'ico-sm-new-window',
    tracking: {
      label: 'about',
      menuItemId: 5
    }
  }
]

export const useUserGuideSidenavStore = defineStore('userGuideSidenav', () => {
  const isOpen = ref(false)
  const isLoading = ref(false)

  const { realmSettings } = useRealmSettings()
  const { user } = useUser()

  const listItems = computedAsync<IUserGuideListItem[]>(async () => {
    let hasGuideline
    try {
      await companyGuidelinesApi.cached.getGuideline(user.value.language)
      hasGuideline = true
    } catch (_) {
      hasGuideline = false
    }
    const isGuidelineEnabled = realmSettings.value?.company_guidelines?.enabled && hasGuideline
    let ret = [ ...initialListItems ]
    if (!isGuidelineEnabled) {
      ret = ret.filter((item) => item.id !== UserGuideListType.COMPANY_GUIDELINES)
    }
    if (!realmSettings.value?.whats_new.enabled) {
      ret = ret.filter((item) => item.id !== UserGuideListType.WHATS_NEW)
    }
    return ret
  }, [], isLoading as any)

  return {
    isOpen,
    isLoading,
    listItems
  }
})
